/**
 * Mobile Analytics Filters Component
 * 
 * Provides filter controls for mobile analytics page:
 * - Period selection (All time, Daily, Weekly, Monthly, Yearly, Custom)
 * - Date range picker with navigation arrows
 * - Collapsible account and user filters
 */

import { ReactElement, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Grid,
  Chip,
  Stack,
  Collapse,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Menu,
  ListItemText,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DateRangePicker from "../../Activity/components/DateRangePicker";
import FilterButtons, { FilterAccount } from "../../Activity/components/FilterButtons";
import { Account } from "../../../types";

export interface MobileAnalyticsFiltersProps {
  // Date filters
  fromDate: string;
  toDate: string;
  onFromDateChange: (date: string) => void;
  onToDateChange: (date: string) => void;
  focusedDateField: "startDate" | "endDate" | null;
  onFocusedDateFieldChange: (field: "startDate" | "endDate" | null) => void;
  
  // Period selection
  selectedPeriod: string;
  onPeriodChange: (period: string) => void;
  onPeriodNavigation: (direction: "left" | "right") => void;
  
  // Account filter
  selectedAccounts: string[];
  onSelectedAccountsChange: (accounts: string[]) => void;
  
  // User filter
  selectedUsers: string[];
  onSelectedUsersChange: (users: string[]) => void;
  
  // Available options
  accounts: Account[];
  workspaceMembers: Array<{ id: string; name?: string; email?: string }>;
  
  // Helpers
  getAccountDisplayName: (accountId: string) => string;
  getUserName: (userId: string) => string;
  
  // Mobile state
  filtersExpanded: boolean;
  onFiltersExpandedChange: (expanded: boolean) => void;
  
  // Filter state
  hasActiveFilters: boolean;
  onClearAllFilters: () => void;
}

/**
 * Mobile Analytics Filters Component
 */
export default function MobileAnalyticsFilters({
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  focusedDateField,
  onFocusedDateFieldChange,
  selectedPeriod,
  onPeriodChange,
  onPeriodNavigation,
  selectedAccounts,
  onSelectedAccountsChange,
  selectedUsers,
  onSelectedUsersChange,
  accounts,
  workspaceMembers,
  getAccountDisplayName,
  getUserName,
  filtersExpanded,
  onFiltersExpandedChange,
  hasActiveFilters,
  onClearAllFilters,
}: MobileAnalyticsFiltersProps): ReactElement {
  
  // State for period menu anchor
  const [periodMenuAnchor, setPeriodMenuAnchor] = useState<null | HTMLElement>(null);

  // Convert accounts to FilterAccount format
  const filterAccounts: FilterAccount[] = useMemo(() => {
    return accounts.map(account => ({
      id: account.id,
      accountName: account.accountName,
      group: account.group,
    }));
  }, [accounts]);

  return (
    <Paper
      elevation={1}
      sx={{
        p: 1.5,
        mb: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        position: "sticky",
        top: 0,
        zIndex: 10,
        bgcolor: "background.paper",
      }}
    >

      {/* Period Display with Navigation - Always Visible */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", mb: filtersExpanded ? 1.5 : 0, justifyContent: "center" }}>
        {/* Previous Period Button */}
        <IconButton
          size="small"
          onClick={() => onPeriodNavigation("left")}
          disabled={selectedPeriod === "Custom"}
          sx={{ 
            border: 1, 
            borderColor: "divider", 
            borderRadius: 1,
            minWidth: 40,
            minHeight: 40,
            opacity: selectedPeriod === "All time" ? 0.5 : 1,
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        
        {/* Period Text Display with Dropdown Arrow */}
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", minWidth: 0 }}>
          {selectedPeriod === "Custom" ? (
            <DateRangePicker
              startDate={fromDate}
              endDate={toDate}
              onStartDateChange={onFromDateChange}
              onEndDateChange={onToDateChange}
              focusedDateField={focusedDateField}
              onFocusedDateFieldChange={onFocusedDateFieldChange}
            />
          ) : (
            <Box 
              onClick={(e) => {
                setPeriodMenuAnchor(e.currentTarget);
              }}
              sx={{ 
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                px: 1,
                py: 0.5,
                borderRadius: 1,
                "&:hover": {
                  bgcolor: "action.hover",
                }
              }}
            >
              <Typography 
                variant="body1" 
                fontWeight="medium" 
                sx={{ 
                  fontSize: "0.95rem", 
                  userSelect: "none", 
                  textAlign: "center",
                }}
              >
                {(() => {
                  if (!fromDate || !toDate) return "";
                  const from = new Date(fromDate);
                  const to = new Date(toDate);
                  
                  if (selectedPeriod === "All time") {
                    return "All time";
                  } else if (selectedPeriod === "Daily") {
                    return from.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                  } else if (selectedPeriod === "Weekly") {
                    const weekStart = from.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                    const weekEnd = to.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                    return `${weekStart} - ${weekEnd}`;
                  } else if (selectedPeriod === "Monthly") {
                    return from.toLocaleDateString("en-US", { month: "short", year: "numeric" });
                  } else if (selectedPeriod === "Yearly") {
                    return from.getFullYear().toString();
                  }
                  return "";
                })()}
              </Typography>
              <ArrowDropDownIcon sx={{ fontSize: "1.2rem", opacity: selectedPeriod === "All time" ? 0.3 : 0.7 }} />
            </Box>
          )}
        </Box>
        
        {/* Period Selection Menu */}
        <Menu
          anchorEl={periodMenuAnchor}
          open={Boolean(periodMenuAnchor)}
          onClose={() => setPeriodMenuAnchor(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <MenuItem onClick={() => { onPeriodChange("Daily"); setPeriodMenuAnchor(null); }}>
            <ListItemText primary="Daily" />
          </MenuItem>
          <MenuItem onClick={() => { onPeriodChange("Weekly"); setPeriodMenuAnchor(null); }}>
            <ListItemText primary="Weekly" />
          </MenuItem>
          <MenuItem onClick={() => { onPeriodChange("Monthly"); setPeriodMenuAnchor(null); }}>
            <ListItemText primary="Monthly" />
          </MenuItem>
          <MenuItem onClick={() => { onPeriodChange("Yearly"); setPeriodMenuAnchor(null); }}>
            <ListItemText primary="Yearly" />
          </MenuItem>
          <MenuItem onClick={() => { onPeriodChange("All time"); setPeriodMenuAnchor(null); }}>
            <ListItemText primary="All time" />
          </MenuItem>
          <MenuItem onClick={() => { onPeriodChange("Custom"); setPeriodMenuAnchor(null); }}>
            <ListItemText primary="Custom Period" />
          </MenuItem>
        </Menu>
        
        {/* Next Period Button */}
        <IconButton
          size="small"
          onClick={() => onPeriodNavigation("right")}
          disabled={selectedPeriod === "Custom"}
          sx={{ 
            border: 1, 
            borderColor: "divider", 
            borderRadius: 1,
            minWidth: 40,
            minHeight: 40,
            opacity: selectedPeriod === "All time" ? 0.5 : 1,
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
      
      {/* Filter panel - collapsible */}
      <Collapse in={filtersExpanded}>
        <Box sx={{ pt: 1.5, pb: focusedDateField ? "420px" : 0 }}>
          <Stack spacing={1.5}>
            <Grid container spacing={1.5}>
              {/* Account filter */}
              {accounts.length > 0 && (
                <Grid size={6}>
                  <FilterButtons
                    accounts={filterAccounts}
                    selectedAccountFilters={selectedAccounts}
                    onAccountFiltersChange={onSelectedAccountsChange}
                    getAccountDisplayName={getAccountDisplayName}
                    workspaceMembers={[]}
                    selectedUsers={[]}
                    onUsersChange={() => {}}
                    getUserName={() => ""}
                    showOnlyEdited={false}
                    onShowOnlyEditedChange={() => {}}
                    showAccountFilter={true}
                    showUserFilter={false}
                    showEditedCheckbox={false}
                  />
                </Grid>
              )}
              
              {/* User filter */}
              <Grid size={6}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <FilterButtons
                    accounts={[]}
                    selectedAccountFilters={[]}
                    onAccountFiltersChange={() => {}}
                    getAccountDisplayName={() => ""}
                    workspaceMembers={workspaceMembers}
                    selectedUsers={selectedUsers}
                    onUsersChange={onSelectedUsersChange}
                    getUserName={getUserName}
                    showOnlyEdited={false}
                    onShowOnlyEditedChange={() => {}}
                    showAccountFilter={false}
                    showUserFilter={true}
                    showEditedCheckbox={false}
                  />
                </Box>
              </Grid>
            </Grid>
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
}
